/**
 * Уход из аккаунта — выход и удаление — одним путём: на витрину, с полной
 * перезагрузкой страницы и одной строкой «вы вышли» уже там.
 *
 * ПОЧЕМУ ПЕРЕЗАГРУЗКА, а не переход внутри приложения. Раньше выход только
 * переворачивал хранилище сессии в «аноним», и первым на это отвечал `AuthGate`
 * экрана, с которого вышли: он уводил на `/signin?next=/cabinet/settings` —
 * человек, закончивший работу, оказывался на форме входа, которая обещала
 * вернуть его в настройки. Состояние прошлой сессии при этом оставалось в
 * памяти вкладки: поток событий, кеши экранов, привязка моделлера на уровне
 * модуля. На общем компьютере следующий человек садится именно за эту вкладку.
 * Новая загрузка страницы не оставляет от прошлого аккаунта ничего — ни
 * соединения, ни данных, — и это дешевле, чем помнить, что обнулить.
 *
 * «Вы вышли» переживает перезагрузку в sessionStorage этой вкладки: одна
 * пометка, которую шапка витрины читает и стирает (`takeFarewell`).
 */
import type { Lang } from '@/i18n/translations'
import { ApiError, request } from '@/shared/api/client'

const FAREWELL_KEY = 'achp-farewell'

export type Farewell = 'signed-out' | 'deleted'

/** Корень витрины на языке, на котором человек работал. */
export function storefrontRoot(lang: Lang): '/' | '/ru' {
  return lang === 'ru' ? '/ru' : '/'
}

/** Уходит на витрину с пометкой для строки «вы вышли». */
export function leaveTo(lang: Lang, farewell: Farewell): void {
  try {
    sessionStorage.setItem(FAREWELL_KEY, farewell)
  } catch {
    /* без пометки — просто без строки; уход важнее */
  }
  window.location.replace(storefrontRoot(lang))
}

/**
 * Выход. `false` — сервер не ответил, и уходить нельзя: кука, возможно, жива,
 * и витрина тут же вернула бы человека в кабинет — «нажал выйти, а я внутри».
 * 401 — сессии уже нет, это тот же результат, что и выход.
 */
export async function signOutAndLeave(lang: Lang): Promise<boolean> {
  try {
    await request<void>('/auth/logout', { method: 'POST', quiet401: true })
  } catch (error) {
    if (!(error instanceof ApiError && error.status === 401)) return false
  }
  leaveTo(lang, 'signed-out')
  return true
}

/** Пароль для удаления не подошёл (сервер отвечает на это 401). */
export class WrongPasswordError extends Error {
  constructor() {
    super('wrong password')
    this.name = 'WrongPasswordError'
  }
}

/**
 * Удаление аккаунта — необратимо, сервер оставляет только то, что одновременно
 * чужая история (см. «Deleting an account» в CODEBASE.md). Неверный пароль —
 * 401, и он ТИХИЙ: объявленный всему приложению, он выводил человека из
 * аккаунта за опечатку в поле подтверждения. Ушло — уходим и мы, на витрину.
 */
export async function deleteAccountAndLeave(password: string, lang: Lang): Promise<void> {
  try {
    await request<void>('/me', {
      method: 'DELETE',
      body: { password: password || null, lang },
      quiet401: true,
    })
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) throw new WrongPasswordError()
    throw error
  }
  leaveTo(lang, 'deleted')
}

/** Пометка «вы вышли» — один раз, на той странице, куда привёл уход. */
export function takeFarewell(): Farewell | null {
  try {
    const value = sessionStorage.getItem(FAREWELL_KEY)
    if (value === null) return null
    sessionStorage.removeItem(FAREWELL_KEY)
    return value === 'deleted' || value === 'signed-out' ? value : null
  } catch {
    return null
  }
}
